import { Box, Typography, Grid, Button, Alert, IconButton, Tooltip } from "@mui/material";

import PropTypes from "prop-types";

const calculateHeaderHeight = (propsHeaderHeight, title, subtitle) => {
  if (propsHeaderHeight) {
    return propsHeaderHeight;
  }

  let headerHeight = title || subtitle ? 15 : 0;

  if (title) {
    headerHeight += 30;
  }

  if (subtitle) {
    headerHeight += 20;
  }

  return headerHeight;
};

const calculateActionsHeight = (propsActionsHeight, actions) => {
  if (propsActionsHeight) {
    return propsActionsHeight;
  }

  if (!actions) {
    return 0;
  }

  if (actions.alert) {
    return 60;
  }

  return 45;
};

const shadow = "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)";
const shadowRaised =
  "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)";

const HubCard = (props) => {
  const {
    cardSx,
    containerSx,
    title,
    titleSx,
    subtitle,
    header, //When using header, the headerHeight prop is required
    headerAlign,
    headerSx,
    actions,
    height,
    minHeight,
    width,
    minWidth,
    padding,
    bgcolor,
    raised,
    fixedHeader = true,
    fixedFooter = true,
    onClick,
    overflow,
  } = props;

  const headerHeight = calculateHeaderHeight(props.headerHeight, title, subtitle);
  const actionsHeight = calculateActionsHeight(props.actionsHeight, actions);

  return (
    <Box
      sx={{
        height: height || (fixedHeader ? "100%" : undefined),
        minHeight,
        width: width || "100%",
        minWidth,
        p: padding,
        bgcolor: bgcolor || "white",
        boxShadow: raised ? shadowRaised : shadow,
        position: "relative",
        ...cardSx,
      }}
      onClick={onClick}
    >
      <Grid
        container
        p={2}
        height={actionsHeight ? `calc(100% - ${actionsHeight}px)` : "100%"}
        position="relative"
        sx={containerSx}
      >
        {header && header}

        {!header && (title || subtitle) && (
          <Grid size={{xs: 12}} textAlign={headerAlign} height={headerHeight} sx={headerSx}>
            {title && (
              <Typography variant="h5" sx={titleSx}>
                {title}
              </Typography>
            )}

            {subtitle && typeof subtitle === "string" && <Typography variant="subtitle1">{subtitle}</Typography>}

            {subtitle && typeof subtitle !== "string" && subtitle}
          </Grid>
        )}

        <Grid size={{xs: 12}} height={`calc(100% - ${fixedFooter ? headerHeight : 0}px)`} overflow={overflow || "auto"}>
          {props.children}
        </Grid>
      </Grid>

      {actions && (
        <Grid
          container
          alignItems="center"
          justifyContent="end"
          spacing={3}
          height={fixedFooter && actionsHeight}
          bgcolor={bgcolor}
          p="0 15px 10px 15px"
        >
          {actions.alert && (
            <Grid size={{xs: "grow"}}>
              <Alert severity={actions.alert.type}>{actions.alert.message}</Alert>
            </Grid>
          )}

          {actions.buttons && (
            <Grid>
              <Grid container spacing={2} justifyContent="end" width="100%">
                {actions.buttons.map((button, index) => (
                  <Grid key={index}>
                    <Tooltip title={button.tooltip}>
                      {button.loading !== undefined ? (
                        <span>
                          <Button
                            variant={button.variant || "contained"}
                            size={button.size || "small"}
                            onClick={button.onClick}
                            loading={button.loading}
                            disabled={button.disabled}
                            sx={{ mr: button.mr }}
                          >
                            {button.title}
                          </Button>
                        </span>
                      ) : button.icon ? (
                        <span>
                          <IconButton
                            size={button.size || "small"}
                            onClick={button.onClick}
                            disabled={button.disabled}
                            sx={{ mr: button.mr }}
                            color="primary"
                          >
                            {button.icon}
                          </IconButton>
                        </span>
                      ) : (
                        <span>
                          <Button
                            variant={button.variant || "contained"}
                            size={button.size || "small"}
                            onClick={button.onClick}
                            disabled={button.disabled}
                            sx={{ mr: button.mr }}
                          >
                            {button.title}
                          </Button>
                        </span>
                      )}
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

HubCard.propTypes = {
  children: PropTypes.node,
  header: PropTypes.node,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
  titleSx: PropTypes.object,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  headerHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  headerSx: PropTypes.object,
  align: PropTypes.bool,
  actions: PropTypes.object,
  actionsHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bgcolor: PropTypes.string,
  raised: PropTypes.bool,
  fixedHeader: PropTypes.bool,
  fixedFooter: PropTypes.bool,
  cardSx: PropTypes.object,
  containerSx: PropTypes.object,
  onClick: PropTypes.func,
  overflow: PropTypes.string,
};

export default HubCard;
